'use client';

import type { EvaluationContext, FeatureFlagAdapter, FlagEvaluation } from './adapter.types';
import { noMatch } from './adapter.types';

import type { FeatureFlagKey } from '@/feature-flags/consts/flags.const';
import { FEATURES, getFeatureNamesByFlagKey, type FeatureName } from '@/feature-flags/consts/features.const';

export const E2E_FEATURE_FLAGS_WINDOW_KEY = '__MEDPREV_E2E_FEATURE_FLAGS__';

export interface E2EFeatureFlagSnapshot {
  features: Partial<Record<FeatureName, unknown>>;
  noMatch?: FeatureName[];
  error?: boolean;
}

declare global {
  interface Window {
    [E2E_FEATURE_FLAGS_WINDOW_KEY]?: E2EFeatureFlagSnapshot;
  }
}

export function getE2EFeatureFlagSnapshot(): E2EFeatureFlagSnapshot | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window[E2E_FEATURE_FLAGS_WINDOW_KEY];
}

export function hasE2EFeatureFlagSnapshot(): boolean {
  return Boolean(getE2EFeatureFlagSnapshot());
}

export class StaticFeatureFlagAdapter implements FeatureFlagAdapter {
  readonly name = 'static';

  private snapshot: E2EFeatureFlagSnapshot;

  constructor(snapshot: E2EFeatureFlagSnapshot) {
    this.snapshot = snapshot;
  }

  async initialize(): Promise<void> {}

  async evaluate(flagKey: string, _context: EvaluationContext): Promise<FlagEvaluation> {
    if (this.snapshot.error) {
      throw new Error('StaticFeatureFlagAdapter: simulated evaluation error');
    }

    const names = getFeatureNamesByFlagKey(flagKey as FeatureFlagKey);
    const provided = names.filter((name) => name in this.snapshot.features);
    const forcedNoMatch = provided.some((name) => this.snapshot.noMatch?.includes(name));

    if (provided.length === 0 || forcedNoMatch) {
      return noMatch(flagKey);
    }

    const attachment = buildAttachment(names, provided, this.snapshot.features);
    const hasActiveFeature = provided.some((name) => this.snapshot.features[name] === true);

    return {
      flagKey,
      variant: hasActiveFeature ? 'treatment' : 'control',
      attachment,
      match: true,
    };
  }
}

function buildAttachment(
  names: FeatureName[],
  provided: FeatureName[],
  features: Partial<Record<FeatureName, unknown>>,
): string {
  const payloadName = provided.find((name) => FEATURES[name].attachmentKey === null);

  if (payloadName) {
    return JSON.stringify(features[payloadName]);
  }

  const config = names.reduce<Record<string, boolean>>((accumulator, name) => {
    const { attachmentKey } = FEATURES[name];

    if (attachmentKey) {
      accumulator[attachmentKey] = features[name] === true;
    }

    return accumulator;
  }, {});

  return JSON.stringify(config);
}
